import React, { useMemo, useState } from 'react';
import { getNetworkFromChainId } from '@dapp/multi-lib/network';
import { useIsMobile } from '@dapp/multi-lib/ui/shared-hooks/useIsMobile';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { ModalV2 } from '@dapp/multi-lib/ui/V2Modal';
import { yupResolver } from '@hookform/resolvers/yup';
import { ethers } from 'ethers';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Paragraph from 'structure/launchpad/components/Typography';
import { useChainBlock } from 'utils/hooks/useChainBlock';
import { ActionSteps } from 'structure/staking/components/ActionSteps';
import { Button } from '@dapp/multi-lib/sdaoui/Button';
import { InputTokenAmount } from 'structure/staking/components/form/InputTokenAmount';
import { StakeDetails } from 'structure/staking/components/StakeDetails';
import {
  useStakingDepositCalcInfo,
  useStakingPoolData,
  useStakingPoolList,
  useStakingUserPoolInfo,
  useUpdatePoolMetadata,
  useWalletSREP,
} from 'structure/staking/hooks/useStakingAPI';
import { useDeposit } from 'structure/staking/hooks/useStakingContract';
import { createStakeValidation } from 'structure/staking/utils/createValidationSchema';
import {
  calcCurrentLockedPeriodInSeconds,
  calcNewLockedPeriodInSeconds,
} from 'structure/staking/utils/userPollInfo.utils';
import { QueryKey } from 'utils/constants/query-keys.enum';
import { daysToSeconds, secondsToDays } from 'utils/date';
import { formatUnits, parseUnits } from 'utils/units';
import { waitForTransaction } from 'utils/waitForTransaction';
import { useConnection } from 'wagmi';
import { FormControl } from './../components/form/FormControl';
import { Input } from './../components/form/Input';
import { Typography } from '@dapp/multi-lib/sdaoui/Typography';
import { formatDateDayMonYear, unixTsToDate } from 'utils/formateDate';
import { RadioButtons } from '@dapp/multi-lib/sdaoui/RadioButtons';
import { InputLockedPeriod } from 'structure/staking/components/form/InputLockedPeriod';
import { ModeLockedPeriod, ModeTokenAmount } from 'structure/staking/types';
import { useAllowance, useApprove } from 'utils/hooks/useERC20Contract';
import { Title4 } from 'components/Typography';
import { getStaticAssetUrl } from 'utils/url';
import { StakingPool } from 'api/StakingAPI';
import { useTokenBalance } from 'utils/hooks/useTokenBalance';

type FormData = {
  tokenAmount?: string;
  lockedPeriod?: number;
};

type DepositModalProps = {
  open: boolean;
  poolId: number;
  onOpenChange: (isOpen: boolean) => void;
};

export function ModalStakeMore({
  open,
  poolId,
  onOpenChange,
}: DepositModalProps) {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile(834);
  const toastV2 = useToast();
  const account = useConnection();
  const params = useParams<{ id: string }>();
  const { data: chainBlock } = useChainBlock({
    queryKey: [QueryKey.CHAIN_BLOCK],
    watch: false,
  });

  const { data = [] } = useStakingPoolList(
    {
      query: {},
    },
    params?.id ?? ''
  );

  const initialPool = data.find((pool) => pool.id === poolId);
  const stakingData = useStakingPoolData(initialPool as StakingPool);
  const pool = stakingData?.data;
  const networkInfo = getNetworkFromChainId(pool.chainId);

  const { data: userBalance } = useTokenBalance({
    address: account.address,
    token: pool.depositTokenAddress,
    chainId: pool.chainId,
    query: {
      enabled: !!account.address && open,
    },
  });

  const userPoolInfo = useStakingUserPoolInfo({
    poolContractAddress: pool.stakingContractAddress,
    walletAddress: account.address as `0x${string}`,
    chainId: pool.chainId,
    pool,
  });

  const currentLockedPeriodInSeconds = calcCurrentLockedPeriodInSeconds(
    userPoolInfo.data,
    chainBlock.timestamp
  );

  const maxExtendDays = Math.floor(
    secondsToDays(
      Number(pool.MAX_LOCKING_PERIOD) - currentLockedPeriodInSeconds
    )
  );

  const [amountMode, setAmountMode] = useState(ModeTokenAmount.KEEP_CURRENT);
  const [lockedPeriodMode, setLockedPeriodMode] = useState(
    ModeLockedPeriod.KEEP_CURRENT
  );

  const isModeStakeMoreTokens = amountMode === ModeTokenAmount.STAKE_MORE;
  const isModeExtendLockedPeriod = lockedPeriodMode === ModeLockedPeriod.EXTEND;
  const isModeStakeMoreTokensOnly =
    !isModeExtendLockedPeriod && isModeStakeMoreTokens;
  const isModeExtendLockedPeriodOnly =
    !isModeStakeMoreTokens && isModeExtendLockedPeriod;

  const isBothModes = isModeStakeMoreTokens && isModeExtendLockedPeriod;
  const isInitialState = !isModeStakeMoreTokens && !isModeExtendLockedPeriod;

  const schema = createStakeValidation({
    tokenAmount: {
      label: t('STAKING.TOKEN_AMOUNT'),
      min:
        isBothModes || isModeStakeMoreTokensOnly
          ? ethers.utils.formatUnits(1, pool.depositTokenDecimals)
          : '0',
      max: formatUnits(userBalance?.value || 0, pool.depositTokenDecimals),
      decimals: pool.depositTokenDecimals,
      required: isModeStakeMoreTokensOnly || isInitialState,
    },
    lockedPeriod: {
      label: t('STAKING.LOCKED_PERIOD'),
      min: isBothModes || isModeExtendLockedPeriodOnly ? 1 : 0,
      max: maxExtendDays,
      required: isModeExtendLockedPeriodOnly || isInitialState,
    },
  });

  const form = useForm<FormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema) as any,
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues: {
      tokenAmount: '0',
      lockedPeriod: 0,
    },
  });

  const lockedPeriod = form.watch('lockedPeriod');
  const tokenAmount = form.watch('tokenAmount');

  const bnTokenAmount = parseUnits(
    tokenAmount || '0',
    pool.depositTokenDecimals
  );

  const handleClickMax = () => {
    form.setValue(
      'tokenAmount',
      formatUnits(userBalance?.value || 0, pool.depositTokenDecimals)
    );

    form.trigger('tokenAmount');
  };

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
  };

  const handleChangeLockedPeriod = (value: number) => {
    form.setValue('lockedPeriod', value);
    form.trigger('lockedPeriod');
  };

  const approve = useApprove({
    tokenAmount: bnTokenAmount.toString(),
    tokenAddress: pool.depositTokenAddress,
    spenderAddress: pool.stakingContractAddress,
    chainId: pool.chainId,
    query: {
      enabled: form.formState.isValid && open,
    },
  });

  const allowance = useAllowance({
    tokenAddress: pool.depositTokenAddress,
    addressOwner: account.address as `0x${string}`,
    addressSpender: pool.stakingContractAddress,
    chainId: pool.chainId,
    query: {
      enabled: account.address && open,
    },
  });

  const bnAllowanceAmount = ethers.BigNumber.from(allowance.data || 0);
  const requiredAllowance = bnTokenAmount.gt(bnAllowanceAmount);

  const newLockedPeriod = calcNewLockedPeriodInSeconds(
    userPoolInfo.data,
    daysToSeconds(lockedPeriod),
    chainBlock.timestamp
  );

  const deposit = useDeposit({
    contractAddress: pool.stakingContractAddress,
    tokenAmount: bnTokenAmount.toString(),
    lockedPeriod: newLockedPeriod,
    enabled: form.formState.isValid && !requiredAllowance && open,
  });

  const walletSREPData = useWalletSREP({
    walletAddress: account.address as `0x${string}`,
    query: {
      enabled: !!account.address && open,
    },
  });

  const updatePoolData = useUpdatePoolMetadata();

  const handleDeposit = async () => {
    if (deposit.prepare.isError) {
      toastV2({
        title: deposit.prepare.error.message,
        description: t('STAKING.FAILED_TO_REQUEST_DEPOSIT'),
        type: 'error',
      });
      return;
    }

    try {
      const result = await deposit.contract.writeContractAsync(
        deposit.prepare.data!.request
      );

      await waitForTransaction({
        chainId: pool.chainId,
        hash: result,
        connectorId: account.connector?.id,
      });

      await updatePoolData.mutateAsync();

      toastV2({
        title: t('STAKING.DEPOSIT_SUCCESS'),
        description: `Code: ${result}`,
        type: 'success',
      });

      await walletSREPData.refetch();
      handleOpenChange(false);
    } catch (error) {
      console.log('### deposit', deposit);
      console.log('### error', error);
      toastV2({
        title: t('ERROR_MESSAGES.ERROR'),
        description: t('STAKING.FAILED_TO_REQUEST_DEPOSIT'),
        type: 'error',
      });
    }
  };

  const stakingCalculation = useStakingDepositCalcInfo({
    poolContractAddress: pool.stakingContractAddress,
    walletAddress: account.address as `0x${string}`,
    chainId: pool.chainId,
    lockedPeriod: daysToSeconds(lockedPeriod),
    tokenAmount: bnTokenAmount.toString(),
    pool,
    query: {
      enabled: !!account.address && open,
    },
  });

  const handleApprove = async () => {
    if (approve.prepare.isError) {
      toastV2({
        title: approve.prepare.error.message,
        description: t('STAKING.FAILED_TO_REQUEST_APPROVE'),
        type: 'error',
      });
      return;
    }
    try {
      const result = await approve.contract.writeContractAsync(
        approve.prepare.data!.request
      );

      await waitForTransaction({
        chainId: pool.chainId,
        hash: result,
        connectorId: account.connector?.id,
      });

      toastV2({
        title: t('STAKING.APPROVE_SUCCESS'),
        description: `Code: ${result}`,
        type: 'success',
      });

      await allowance.refetch();
    } catch (error) {
      console.log('### error', error);
      toastV2({
        title: t('ERROR_MESSAGES.ERROR'),
        description: t('STAKING.FAILED_TO_REQUEST_APPROVE'),
        type: 'error',
      });
    }
  };

  const isLockedPeriodLimitReached = maxExtendDays === 0;

  const maxLockingPeriod = useMemo(() => {
    return secondsToDays(Number(pool.MAX_LOCKING_PERIOD));
  }, [pool]);

  const handleSubmit = async () => {
    if (requiredAllowance) {
      return await handleApprove();
    }

    await handleDeposit();
  };

  return (
    <ModalV2.Root open={open} onOpenChange={handleOpenChange}>
      <ModalV2.Content
        variant='dark'
        fullscreen={isMobile}
        scrollable
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className='flex flex-row items-center justify-between'>
            <ModalV2.Title variant='dark'>
              <Title4>{t('STAKING.MODAL_STAKE_MORE_TITLE')}</Title4>
            </ModalV2.Title>
            <ModalV2.Close variant='dark' />
          </div>
          <ModalV2.Body variant='dark' className='rounded-t-main mb-5'>
            <FormControl
              label={
                <div className='flex flex-row'>
                  <Paragraph className='text-sm text-[#B4BCD0]'>
                    {t('STAKING.NETWORK')}
                  </Paragraph>
                </div>
              }
            >
              <Input
                value=''
                type='text'
                disabled
                startEnhancer={
                  <div className='flex items-center gap-2'>
                    <Image
                      alt={networkInfo.nativeCurrency.symbol}
                      src={getStaticAssetUrl(
                        `/sfi/static/image/token/${networkInfo.nativeCurrency.symbol}.svg`
                      )}
                      height={32}
                      width={32}
                    />
                    <Paragraph className='text-sm font-normal'>
                      {networkInfo.name ?? networkInfo.shortName}
                    </Paragraph>
                  </div>
                }
              />
            </FormControl>

            <div className='gap-2'>
              <div className='flex flex-row justify-between py-2'>
                <Typography className='text-sm'>
                  {t('STAKING.CURRENT_STAKED_AMOUNT')}
                </Typography>
                <Typography className='text-sm'>
                  {t('tokenAmount', {
                    value: formatUnits(
                      userPoolInfo?.data?.totalStaked || 0,
                      pool.depositTokenDecimals
                    ),
                  })}{' '}
                  {pool.depositTokenSymbol}
                </Typography>
              </div>

              <Typography as='p' className='py-2 text-xs text-[#B4BCD0]'>
                {t('STAKING.ADD_MORE')}
              </Typography>

              <RadioButtons<ModeTokenAmount>
                style='dark'
                size='small'
                value={amountMode}
                onChange={(value) => {
                  setAmountMode(value);
                  form.resetField('tokenAmount');
                }}
                options={[
                  {
                    label: t('STAKING.KEEP_CURRENT_AMOUNT'),
                    value: ModeTokenAmount.KEEP_CURRENT,
                  },
                  {
                    label: t('STAKING.ADD_MORE_AMOUNT'),
                    value: ModeTokenAmount.STAKE_MORE,
                  },
                ]}
              />

              {isModeStakeMoreTokens && (
                <InputTokenAmount
                  tokenSymbol={pool.depositTokenSymbol}
                  error={form.formState.errors.tokenAmount?.message}
                  onMax={handleClickMax}
                  label={
                    <div className='flex flex-row justify-end'>
                      <Paragraph className='text-xs	text-[#B4BCD0]'>
                        {t('STAKING.BALANCE')} :{' '}
                        {t('tokenAmount', {
                          value: formatUnits(
                            userBalance?.value || 0,
                            pool.depositTokenDecimals
                          ),
                        })}{' '}
                        {pool.depositTokenSymbol}
                      </Paragraph>
                    </div>
                  }
                  {...form.register('tokenAmount')}
                />
              )}
            </div>

            <div className='my-5 border-b border-[#F7F8F8]' />

            <div className='flex flex-row justify-between'>
              <Typography as='p' className='text-sm'>
                {t('STAKING.CURRENT_UNLOCK_DATE')}
              </Typography>
              <div className='flex flex-col items-end'>
                <Typography className='text-sm'>
                  {formatDateDayMonYear(
                    unixTsToDate(
                      parseInt(userPoolInfo?.data?.unlockDate || '0', 10)
                    )
                  )}
                </Typography>
                <Typography className='text-xs text-[#B4BCD0]'>
                  {Math.ceil(secondsToDays(currentLockedPeriodInSeconds))}{' '}
                  {t('day', {
                    count: Math.ceil(
                      secondsToDays(currentLockedPeriodInSeconds)
                    ),
                  })}
                </Typography>
              </div>
            </div>

            <Typography as='p' className='py-2 text-xs text-[#B4BCD0]'>
              {t('STAKING.EXTEND_UNLOCK_DATE')}
            </Typography>

            <div>
              <RadioButtons<ModeLockedPeriod>
                style='dark'
                size='small'
                value={lockedPeriodMode}
                onChange={(value) => {
                  setLockedPeriodMode(value);
                  form.resetField('lockedPeriod');
                }}
                options={[
                  {
                    label: t('STAKING.KEEP_CURRENT_PERIOD'),
                    value: ModeLockedPeriod.KEEP_CURRENT,
                  },
                  {
                    label: t('STAKING.EXTEND_PERIOD'),
                    value: ModeLockedPeriod.EXTEND,
                  },
                ]}
              />
            </div>

            {isModeExtendLockedPeriod && (
              <div>
                <InputLockedPeriod
                  prefix='+'
                  onChanePeriod={handleChangeLockedPeriod}
                  lockingPeriods={pool.defaultLockingPeriods}
                  error={
                    isLockedPeriodLimitReached &&
                    (form.getValues().lockedPeriod ?? 0) > 0
                      ? t('STAKING.REACH_LIMIT_OF_LOCKED_PERIOD', {
                          value: maxLockingPeriod,
                        })
                      : form.formState.errors.lockedPeriod?.message
                  }
                  {...form.register('lockedPeriod')}
                  value={form.getValues().lockedPeriod}
                />
                <Typography as='p' className='text-xs text-[#B4BCD0]'>
                  {t('STAKING.MAX_EXTENSION_DAYS', { days: maxExtendDays })}{' '}
                  {t('day', {
                    count: maxExtendDays,
                  })}
                </Typography>
              </div>
            )}
          </ModalV2.Body>
          <ModalV2.Body variant='dark' className='rounded-t-main pt-5'>
            <StakeDetails
              pool={pool}
              loading={stakingCalculation.isFetching}
              userPoolInfo={userPoolInfo.data}
              action='stake'
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              calculation={stakingCalculation.data as any}
            />
            <div className='pb-3 pt-4'>
              <ActionSteps
                approve={requiredAllowance}
                deposit={!requiredAllowance}
              />
            </div>
          </ModalV2.Body>
          <div className='mt-6 flex justify-end gap-4'>
            <Button
              type='reset'
              onClick={() => handleOpenChange(false)}
              disabled={
                deposit.contract.isPending || approve.contract.isPending
              }
            >
              {t('STAKING.CANCEL')}
            </Button>
            {!requiredAllowance ? (
              <Button
                type='submit'
                loading={
                  deposit.contract.isPending ||
                  deposit.prepare.isFetching ||
                  form.formState.isSubmitting
                }
                disabled={
                  isInitialState ||
                  deposit.contract.isPending ||
                  deposit.prepare.isFetching ||
                  !form.formState.isValid ||
                  form.formState.isSubmitting
                }
              >
                {isModeExtendLockedPeriodOnly
                  ? t('STAKING.EXTEND')
                  : t('STAKING.DEPOSIT')}
              </Button>
            ) : (
              <Button
                type='submit'
                loading={
                  approve.contract.isPending ||
                  approve.prepare.isFetching ||
                  form.formState.isSubmitting
                }
                disabled={
                  isInitialState ||
                  approve.contract.isPending ||
                  approve.prepare.isFetching ||
                  !form.formState.isValid ||
                  form.formState.isSubmitting
                }
              >
                {t('STAKING.APPROVE')}
              </Button>
            )}
          </div>
        </form>
      </ModalV2.Content>
    </ModalV2.Root>
  );
}
